// =====================================================================
// Taskboard status model — the single source of truth for the four
// board columns (product rule 1: configurable constants, not strings
// scattered through the code).
//
// The redesign uses Open / Waiting / In Progress / Done. The backend's
// DealTracker.status still carries the legacy enum (waiting / accepted /
// rejected) on existing entries; per Marin's decision (2026-07-07) the
// ORIGINAL value is preserved in the data and we only MAP it for
// display:  waiting -> Waiting, accepted -> Done, rejected -> Done.
// New entries and drag/quick-select writes use the canonical values
// below. `normalizeStatus` collapses any raw value to one canonical id
// so the board, timeline and pills all agree.
// =====================================================================

export const STATUS = {
  OPEN: "open",
  WAITING: "waiting",
  IN_PROGRESS: "in_progress",
  DONE: "done",
};

// Column order left-to-right on the board, and the source of the
// timeline/pill styling. Colors are taken verbatim from the design
// export (Open slate, Waiting amber, In Progress blue, Done green).
export const STATUS_COLUMNS = [
  {
    id: STATUS.OPEN,
    label: "Open",
    labelColor: "#475467",
    countBg: "#E4E9F0",
    countColor: "#667085",
    columnBg: "#EEF1F6",
    cardBorder: "#E4E9F0",
    pillBg: "#F2F4F7",
    pillColor: "#475467",
    highlighted: false,
    dimCards: false,
  },
  {
    id: STATUS.WAITING,
    label: "Waiting",
    labelColor: "#B54708",
    countBg: "#FFEFD3",
    countColor: "#B54708",
    columnBg: "#EEF1F6",
    cardBorder: "#F4D8A6",
    pillBg: "#FFF8EB",
    pillColor: "#B54708",
    highlighted: false,
    dimCards: false,
  },
  {
    id: STATUS.IN_PROGRESS,
    label: "In Progress",
    labelColor: "#1866CC",
    countBg: "#DBEAFF",
    countColor: "#1866CC",
    columnBg: "#F0F7FF",
    cardBorder: "#E4E9F0",
    pillBg: "#EAF2FF",
    pillColor: "#1866CC",
    highlighted: true,
    dimCards: false,
  },
  {
    id: STATUS.DONE,
    label: "Done",
    labelColor: "#0E8345",
    countBg: "#DDF3E7",
    countColor: "#0E8345",
    columnBg: "#EEF1F6",
    cardBorder: "#E4E9F0",
    pillBg: "#ECFDF5",
    pillColor: "#047857",
    highlighted: false,
    dimCards: true,
  },
];

// Legacy backend value -> canonical column id. Anything not listed
// (already-canonical values, unknown/null) falls through to OPEN so an
// entry is never lost off the board.
const LEGACY_TO_CANONICAL = {
  open: STATUS.OPEN,
  waiting: STATUS.WAITING,
  in_progress: STATUS.IN_PROGRESS,
  "in progress": STATUS.IN_PROGRESS,
  inprogress: STATUS.IN_PROGRESS,
  done: STATUS.DONE,
  accepted: STATUS.DONE,
  rejected: STATUS.DONE,
};

export function normalizeStatus(raw) {
  if (!raw) return STATUS.OPEN;
  const key = String(raw).trim().toLowerCase();
  return LEGACY_TO_CANONICAL[key] || STATUS.OPEN;
}

// Column placement for an entry. The backend exposes a dedicated
// `board_status` (already one of the four canonical ids, with legacy
// accepted/rejected pre-mapped to done); prefer it and fall back to
// normalizing the raw `status` for any entry that predates the field.
export function entryBoardStatus(entry) {
  if (entry?.board_status) return normalizeStatus(entry.board_status);
  return normalizeStatus(entry?.status);
}

const COLUMN_BY_ID = STATUS_COLUMNS.reduce((acc, c) => {
  acc[c.id] = c;
  return acc;
}, {});

// Column meta for any raw status value (already normalized inside).
export function statusColumn(rawOrId) {
  return COLUMN_BY_ID[normalizeStatus(rawOrId)] || COLUMN_BY_ID[STATUS.OPEN];
}

export function statusLabel(rawOrId) {
  return statusColumn(rawOrId).label;
}

// Entry "type" (mode of communication). The backend enum is fixed to
// phone / email / meeting; we keep the friendlier labels and a safe
// "Other" fallback for any value outside the enum. Adding richer types
// (Offer / Trial) would be an additive backend enum change, out of
// scope for this frontend pass.
export const ENTRY_MODES = [
  { value: "phone", label: "Call" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting" },
];

export function modeLabel(mode) {
  const found = ENTRY_MODES.find((m) => m.value === mode);
  return found ? found.label : "Other";
}
